import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { openRouterApi, authApi, customExpertsApi, adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Key, 
  Loader2, 
  CheckCircle2,
  ExternalLink,
  Wallet,
  Globe,
  RefreshCw,
  Gauge,
  Zap,
  Brain,
  Server,
  Plus,
  Trash2,
  User,
  Pencil,
  Bot,
  Search,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';

// Accuracy levels configuration
const ACCURACY_LEVELS = [
  { 
    id: 'economic', 
    label: { it: 'Economico', en: 'Economic' },
    description: { it: 'Modelli open-source, costo minimo', en: 'Open-source models, minimal cost' },
    icon: '💰',
    color: 'text-success'
  },
  { 
    id: 'base', 
    label: { it: 'Base', en: 'Basic' },
    description: { it: 'Modelli buoni ma economici', en: 'Good but affordable models' },
    icon: '📊',
    color: 'text-info'
  },
  { 
    id: 'intermediate', 
    label: { it: 'Intermedio', en: 'Intermediate' },
    description: { it: 'Bilanciamento qualità/costo', en: 'Quality/cost balance' },
    icon: '⚖️',
    color: 'text-primary'
  },
  { 
    id: 'advanced', 
    label: { it: 'Avanzato', en: 'Advanced' },
    description: { it: 'Modelli premium senza deep thinking', en: 'Premium models without deep thinking' },
    icon: '🚀',
    color: 'text-warning'
  },
  { 
    id: 'maximum', 
    label: { it: 'Massimo', en: 'Maximum' },
    description: { it: 'Migliori modelli + deep thinking', en: 'Best models + deep thinking' },
    icon: '🧠',
    color: 'text-destructive'
  }
];

// Team size configuration
const TEAM_SIZES = [
  { 
    id: 'simplified', 
    label: { it: 'Semplificato', en: 'Simplified' },
    description: { it: '2-3 esperti essenziali', en: '2-3 essential experts' },
    range: '2-3',
    icon: '👤'
  },
  { 
    id: 'normal', 
    label: { it: 'Normale', en: 'Normal' },
    description: { it: '4-7 esperti bilanciati', en: '4-7 balanced experts' },
    range: '4-7',
    icon: '👥'
  },
  { 
    id: 'extended', 
    label: { it: 'Esteso', en: 'Extended' },
    description: { it: '8-12 copertura completa', en: '8-12 comprehensive coverage' },
    range: '8-12',
    icon: '👨‍👩‍👧‍👦'
  },
  { 
    id: 'maximum', 
    label: { it: 'Massimo', en: 'Maximum' },
    description: { it: '13-15 analisi profonda', en: '13-15 deep analysis' },
    range: '13-15',
    icon: '🏛️'
  }
];

// Response length configuration
const RESPONSE_LENGTHS = [
  { 
    id: 'brief', 
    label: { it: 'Breve', en: 'Brief' },
    description: { it: '500-700 parole per esperto', en: '500-700 words per expert' },
    icon: '📝'
  },
  { 
    id: 'medium', 
    label: { it: 'Media', en: 'Medium' },
    description: { it: '1000-1500 parole per esperto', en: '1000-1500 words per expert' },
    icon: '📄'
  },
  { 
    id: 'complete', 
    label: { it: 'Completa', en: 'Complete' },
    description: { it: 'Nessun limite, analisi approfondita', en: 'No limit, in-depth analysis' },
    icon: '📚'
  }
];

export default function SettingsPage() {
  const { t, language, setLanguage, hasOpenRouterKey, openRouterBalance, refreshOpenRouterStatus, user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [accuracyLevel, setAccuracyLevel] = useState('base');
  const [teamSize, setTeamSize] = useState('normal');
  const [responseLength, setResponseLength] = useState('medium');
  const [savingAccuracy, setSavingAccuracy] = useState(false);
  const [savingTeamSize, setSavingTeamSize] = useState(false);
  const [savingResponseLength, setSavingResponseLength] = useState(false);
  
  // Manager del Progetto (MdP) MCP Servers
  const [managerMcpServers, setManagerMcpServers] = useState([]);
  const [savingMcp, setSavingMcp] = useState(false);
  
  // Manager Prompts (3 phases)
  const [managerPrompts, setManagerPrompts] = useState({
    phase1_analysis: '',
    phase2_coordination: '',
    phase3_synthesis: ''
  });
  const [hasCustomPrompts, setHasCustomPrompts] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [savingPhase, setSavingPhase] = useState(null); // 'phase1', 'phase2', 'phase3', or null
  
  // Legacy single prompt (for backward compatibility display)
  const [managerPrompt, setManagerPrompt] = useState('');
  
  // Custom Experts
  const [customExperts, setCustomExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(true);
  const [expertDialogOpen, setExpertDialogOpen] = useState(false);
  const [editingExpert, setEditingExpert] = useState(null);
  const [expertForm, setExpertForm] = useState({
    name: '',
    description: '',
    expertise_area: '',
    is_global: false,
    preferred_model: '',
    mcp_servers: []
  });
  const [savingExpert, setSavingExpert] = useState(false);
  
  // Models for expert dropdown
  const [allModels, setAllModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelSearchOpen, setModelSearchOpen] = useState(false);
  const [modelFilter, setModelFilter] = useState('all'); // 'all' or 'open-source'
  const [modelSearch, setModelSearch] = useState('');
  
  // Default Manager Prompts (3 phases)
  const defaultManagerPrompts = {
    phase1_analysis: language === 'it' 
      ? `Sei l'Analista del Progetto. Il tuo compito è:

1. ANALISI PROFONDA
- Analizza la richiesta dell'utente in modo approfondito
- Identifica i temi chiave e le aree di competenza necessarie
- Fai ricerca sul topic se necessario

2. COMPOSIZIONE TEAM
- Identifica TUTTI gli esperti necessari per rispondere alla richiesta
- Per progetti semplici: 3-5 esperti
- Per media complessità: 5-8 esperti  
- Per progetti complessi: 8-12 esperti
- MAI aggiungere esperti solo per fare numero

3. SELEZIONE MODELLI
- Per ogni esperto, seleziona il modello LLM più appropriato
- Considera il tipo di task (coding, analisi, creativo, ricerca)
- Rispetta il livello di accuratezza selezionato dall'utente

4. DOMANDE DI CHIARIMENTO
- Genera domande per comprendere meglio le esigenze dell'utente
- Categorie: budget, timeline, scope, risorse, vincoli

IMPORTANTE: Questo strumento genera SOLO analisi e report, NON codice funzionante.`
      : `You are the Project Analyst. Your task is:

1. DEEP ANALYSIS
- Analyze the user's request thoroughly
- Identify key themes and required expertise areas
- Research the topic if necessary

2. TEAM COMPOSITION
- Identify ALL experts needed to answer the request
- For simple projects: 3-5 experts
- For medium complexity: 5-8 experts
- For complex projects: 8-12 experts
- NEVER add experts just to fill a quota

3. MODEL SELECTION
- For each expert, select the most appropriate LLM model
- Consider the task type (coding, analysis, creative, research)
- Respect the accuracy level selected by the user

4. CLARIFICATION QUESTIONS
- Generate questions to better understand user needs
- Categories: budget, timeline, scope, resources, constraints

IMPORTANT: This tool generates ONLY analysis and reports, NOT working code.`,

    phase2_coordination: language === 'it'
      ? `Sei il Coordinatore del Team. Il tuo compito è:

1. COORDINAMENTO ESPERTI
- Invia i prompt specifici a ogni esperto del team
- Assicurati che ogni esperto abbia il contesto necessario
- Monitora la qualità delle risposte

2. RACCOLTA PARERI
- Raccogli tutte le risposte degli esperti
- Organizza le informazioni in modo strutturato
- Identifica eventuali lacune nelle risposte

3. CONTROLLO QUALITÀ
- Verifica che ogni esperto abbia risposto in modo completo
- Segnala risposte che potrebbero necessitare approfondimenti

CONTESTO DISPONIBILE:
- Richiesta originale dell'utente
- Team di esperti creato e modelli assegnati
- Motivazioni delle scelte fatte`
      : `You are the Team Coordinator. Your task is:

1. EXPERT COORDINATION
- Send specific prompts to each team expert
- Ensure each expert has the necessary context
- Monitor response quality

2. GATHERING OPINIONS
- Collect all expert responses
- Organize information in a structured way
- Identify any gaps in responses

3. QUALITY CONTROL
- Verify that each expert has responded completely
- Flag responses that may need deeper analysis

AVAILABLE CONTEXT:
- Original user request
- Expert team created and assigned models
- Reasoning behind the choices made`,

    phase3_synthesis: language === 'it'
      ? `Sei il Valutatore e Sintetizzatore finale. Il tuo compito è:

1. VALUTAZIONE QUALITÀ
- Valuta la qualità complessiva delle risposte degli esperti
- Identifica contraddizioni o disaccordi tra esperti
- Verifica se tutte le aree sono state coperte

2. SUGGERIMENTI DI OTTIMIZZAZIONE
- Se un esperto ha dato una risposta povera, suggerisci di sostituirlo o cambiare modello
- Se manca una competenza, suggerisci di aggiungere un esperto
- Se ci sono sovrapposizioni, segnala che si potrebbero ridurre gli esperti

3. SINTESI FINALE
- Sintetizza tutti i pareri in un'analisi coerente
- Fornisci feedback ONESTO sulla fattibilità
- Dai raccomandazioni concrete e actionable
- Suggerisci alternative se il piano originale è rischioso

STRUTTURA DEL REPORT:
## PROBLEMI CRITICI (se presenti)
## ASPETTI POSITIVI
## RISULTATI CHIAVE (da ogni esperto)
## CONTRADDIZIONI TRA ESPERTI (se presenti)
## RACCOMANDAZIONI
## ALTERNATIVE (se il piano è rischioso)
## PROSSIMI PASSI

CONTESTO COMPLETO DISPONIBILE:
- Richiesta originale
- Team creato e modelli scelti
- TUTTI i pareri degli esperti`
      : `You are the Final Evaluator and Synthesizer. Your task is:

1. QUALITY EVALUATION
- Evaluate the overall quality of expert responses
- Identify contradictions or disagreements between experts
- Verify if all areas have been covered

2. OPTIMIZATION SUGGESTIONS
- If an expert gave a poor response, suggest replacing them or changing the model
- If a competency is missing, suggest adding an expert
- If there are overlaps, note that experts could be reduced

3. FINAL SYNTHESIS
- Synthesize all opinions into a coherent analysis
- Provide HONEST feedback on feasibility
- Give concrete, actionable recommendations
- Suggest alternatives if the original plan is risky

REPORT STRUCTURE:
## CRITICAL ISSUES (if any)
## POSITIVE ASPECTS
## KEY FINDINGS (from each expert)
## CONTRADICTIONS BETWEEN EXPERTS (if any)
## RECOMMENDATIONS
## ALTERNATIVES (if plan is risky)
## NEXT STEPS

FULL CONTEXT AVAILABLE:
- Original request
- Team created and models chosen
- ALL expert opinions`
  };
  
  // Legacy default prompt (for backward compatibility)
  const defaultManagerPrompt = language === 'it' 
    ? `Sei il Manager del Progetto, un consulente esperto che coordina un team di specialisti per fornire consulenza strategica.

IL TUO RUOLO:
- Analizzare le richieste degli utenti e identificare quali esperti sono necessari
- Creare dinamicamente un team di esperti appropriato per ogni richiesta
- Coordinare le consulenze e sintetizzare i risultati
- Fornire raccomandazioni strategiche basate sui pareri degli esperti

REGOLE IMPORTANTI:
1. NON generare codice o implementazioni - fornisci SOLO consulenza e analisi
2. Crea il team di esperti in base alla natura della richiesta
3. Ogni esperto deve avere competenze specifiche e complementari
4. Rispondi sempre nella lingua dell'utente
5. Sii pratico e orientato ai risultati`
    : `You are the Project Manager, an expert consultant who coordinates a team of specialists to provide strategic consulting.

YOUR ROLE:
- Analyze user requests and identify which experts are needed
- Dynamically create an appropriate expert team for each request
- Coordinate consultations and synthesize results
- Provide strategic recommendations based on expert opinions

IMPORTANT RULES:
1. DO NOT generate code or implementations - provide ONLY consulting and analysis
2. Create the expert team based on the nature of the request
3. Each expert should have specific and complementary skills
4. Always respond in the user's language
5. Be practical and results-oriented`;

  useEffect(() => {
    loadUserPreferences();
    loadCustomExperts();
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await openRouterApi.models();
      setAllModels(response.data?.models || []);
    } catch (error) {
      // If OpenRouter key is not configured, models will be empty
      // This is expected behavior - user needs to configure API key first
      if (error.response?.status !== 400) {
        console.error('Failed to load models:', error);
      }
      setAllModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const response = await authApi.getPreferences();
      if (response.data?.accuracy_level) {
        setAccuracyLevel(response.data.accuracy_level);
      }
      if (response.data?.team_size) {
        setTeamSize(response.data.team_size);
      }
      if (response.data?.response_length) {
        setResponseLength(response.data.response_length);
      }
      if (response.data?.manager_mcp_servers) {
        setManagerMcpServers(response.data.manager_mcp_servers);
      }
      
      // Load 3-phase prompts
      if (response.data?.manager_prompts) {
        setManagerPrompts({
          phase1_analysis: response.data.manager_prompts.phase1_analysis || defaultManagerPrompts.phase1_analysis,
          phase2_coordination: response.data.manager_prompts.phase2_coordination || defaultManagerPrompts.phase2_coordination,
          phase3_synthesis: response.data.manager_prompts.phase3_synthesis || defaultManagerPrompts.phase3_synthesis
        });
      } else {
        setManagerPrompts(defaultManagerPrompts);
      }
      setHasCustomPrompts(response.data?.has_custom_prompts || false);
      
      // Legacy prompt (for backward compatibility)
      if (response.data?.custom_manager_prompt !== undefined) {
        setManagerPrompt(response.data.custom_manager_prompt || defaultManagerPrompt);
      } else {
        setManagerPrompt(defaultManagerPrompt);
      }
    } catch (error) {
      setManagerPrompt(defaultManagerPrompt);
      setManagerPrompts(defaultManagerPrompts);
    }
  };

  const loadCustomExperts = async () => {
    try {
      const response = await customExpertsApi.list();
      setCustomExperts(response.data);
    } catch (error) {
      console.error('Failed to load custom experts');
    } finally {
      setLoadingExperts(false);
    }
  };

  // Filter models based on search and open-source filter
  const isModelOpenSource = (modelId) => {
    return modelId.includes('llama') || modelId.includes('mistral') || modelId.includes('qwen') || 
           modelId.includes('gemma') || modelId.includes('deepseek') || modelId.includes('nous') || 
           modelId.includes('openchat') || modelId.includes('dolphin') || modelId.includes('yi-');
  };

  const filteredModels = allModels.filter(model => {
    const matchesSearch = model.id.toLowerCase().includes(modelSearch.toLowerCase()) ||
                         model.name?.toLowerCase().includes(modelSearch.toLowerCase());
    const matchesFilter = modelFilter === 'all' || 
                         (modelFilter === 'open-source' && isModelOpenSource(model.id));
    return matchesSearch && matchesFilter;
  });

  // Handle open-source filter toggle
  const handleOpenSourceFilterChange = (checked) => {
    const newFilter = checked ? 'open-source' : 'all';
    setModelFilter(newFilter);
    
    // If switching to open-source and current model is not open-source, clear it
    if (checked && expertForm.preferred_model && !isModelOpenSource(expertForm.preferred_model)) {
      setExpertForm(f => ({ ...f, preferred_model: '' }));
      toast.info(language === 'it' 
        ? 'Modello rimosso perché non è open-source' 
        : 'Model removed because it is not open-source');
    }
  };

  const handleSaveAccuracyLevel = async (level) => {
    setSavingAccuracy(true);
    try {
      await authApi.updatePreferences({ accuracy_level: level });
      setAccuracyLevel(level);
      toast.success(language === 'it' ? 'Livello accuratezza salvato' : 'Accuracy level saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingAccuracy(false);
    }
  };

  const handleSaveTeamSize = async (size) => {
    setSavingTeamSize(true);
    try {
      await authApi.updatePreferences({ team_size: size });
      setTeamSize(size);
      toast.success(language === 'it' ? 'Dimensione team salvata' : 'Team size saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingTeamSize(false);
    }
  };

  const handleSaveResponseLength = async (length) => {
    setSavingResponseLength(true);
    try {
      await authApi.updatePreferences({ response_length: length });
      setResponseLength(length);
      toast.success(language === 'it' ? 'Lunghezza pareri salvata' : 'Response length saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingResponseLength(false);
    }
  };

  const handleSaveManagerPrompt = async () => {
    setSavingPrompt(true);
    try {
      await authApi.updatePreferences({ custom_manager_prompt: managerPrompt });
      toast.success(language === 'it' ? 'Prompt Manager salvato' : 'Manager prompt saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingPrompt(false);
    }
  };

  // Save individual phase prompt
  const handleSavePhasePrompt = async (phase) => {
    setSavingPhase(phase);
    try {
      await authApi.updatePreferences({ 
        manager_prompts: { 
          [phase]: managerPrompts[phase] 
        } 
      });
      setHasCustomPrompts(true);
      toast.success(language === 'it' ? 'Prompt fase salvato' : 'Phase prompt saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingPhase(null);
    }
  };

  // Save all phase prompts at once
  const handleSaveAllPrompts = async () => {
    setSavingPrompt(true);
    try {
      await authApi.updatePreferences({ manager_prompts: managerPrompts });
      setHasCustomPrompts(true);
      toast.success(language === 'it' ? 'Tutti i prompt salvati' : 'All prompts saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingPrompt(false);
    }
  };

  // Reset prompts to global (for regular users)
  const handleResetToGlobal = async () => {
    try {
      await authApi.resetPromptsToGlobal();
      setHasCustomPrompts(false);
      // Reload preferences to get global prompts
      await loadUserPreferences();
      toast.success(language === 'it' ? 'Prompt ripristinati ai valori globali' : 'Prompts reset to global values');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel ripristino' : 'Failed to reset');
    }
  };

  // Reset to default values (local reset without saving)
  const resetManagerPrompt = () => {
    setManagerPrompt(defaultManagerPrompt);
    setManagerPrompts(defaultManagerPrompts);
    toast.info(language === 'it' ? 'Prompt ripristinati ai default' : 'Prompts reset to defaults');
  };

  const openNewExpertDialog = () => {
    setEditingExpert(null);
    setExpertForm({ name: '', description: '', expertise_area: '', is_global: false, preferred_model: '', mcp_servers: [] });
    setModelSearch('');
    setExpertDialogOpen(true);
  };

  const openEditExpertDialog = (expert) => {
    setEditingExpert(expert);
    setExpertForm({
      name: expert.name,
      description: expert.description,
      expertise_area: expert.expertise_area,
      is_global: expert.is_global,
      preferred_model: expert.preferred_model || '',
      mcp_servers: expert.mcp_servers || []
    });
    setModelSearch('');
    setExpertDialogOpen(true);
  };

  const addMcpServerToExpert = () => {
    setExpertForm(f => ({
      ...f,
      mcp_servers: [...f.mcp_servers, { name: '', url: '', api_key: '', description: '' }]
    }));
  };

  const removeMcpServerFromExpert = (index) => {
    setExpertForm(f => ({
      ...f,
      mcp_servers: f.mcp_servers.filter((_, i) => i !== index)
    }));
  };

  const updateExpertMcpServer = (index, field, value) => {
    setExpertForm(f => {
      const newServers = [...f.mcp_servers];
      newServers[index] = { ...newServers[index], [field]: value };
      return { ...f, mcp_servers: newServers };
    });
  };

  const handleSaveExpert = async () => {
    if (!expertForm.name || !expertForm.description || !expertForm.expertise_area) {
      toast.error(language === 'it' ? 'Compila tutti i campi obbligatori' : 'Fill all required fields');
      return;
    }
    
    setSavingExpert(true);
    try {
      if (editingExpert) {
        await customExpertsApi.update(editingExpert.id, expertForm);
        toast.success(language === 'it' ? 'Esperto aggiornato' : 'Expert updated');
      } else {
        await customExpertsApi.create(expertForm);
        toast.success(language === 'it' ? 'Esperto creato' : 'Expert created');
      }
      setExpertDialogOpen(false);
      loadCustomExperts();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingExpert(false);
    }
  };

  const handleDeleteExpert = async (id) => {
    try {
      await customExpertsApi.delete(id);
      toast.success(language === 'it' ? 'Esperto eliminato' : 'Expert deleted');
      loadCustomExperts();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'eliminazione' : 'Failed to delete');
    }
  };

  const addManagerMcpServer = () => {
    setManagerMcpServers(prev => [...prev, { name: '', url: '', api_key: '', description: '' }]);
  };

  const removeManagerMcpServer = (index) => {
    setManagerMcpServers(prev => prev.filter((_, i) => i !== index));
  };

  const updateManagerMcpServer = (index, field, value) => {
    setManagerMcpServers(prev => {
      const newServers = [...prev];
      newServers[index] = { ...newServers[index], [field]: value };
      return newServers;
    });
  };

  const handleSaveManagerMcpServers = async () => {
    setSavingMcp(true);
    try {
      await authApi.updatePreferences({ manager_mcp_servers: managerMcpServers });
      toast.success(language === 'it' ? 'MCP Servers MdP salvati' : 'Manager MCP Servers saved');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingMcp(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      toast.error(language === 'it' ? 'Inserisci una API key' : 'Enter an API key');
      return;
    }

    setSaving(true);
    try {
      const response = await openRouterApi.setup(apiKey);
      toast.success(language === 'it' ? 'API Key salvata con successo!' : 'API Key saved successfully!');
      setApiKey('');
      await refreshOpenRouterStatus();
      // Reload models after API key is saved
      await loadModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'it' ? 'API Key non valida' : 'Invalid API Key'));
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshBalance = async () => {
    setRefreshing(true);
    try {
      await refreshOpenRouterStatus();
      toast.success(language === 'it' ? 'Saldo aggiornato' : 'Balance refreshed');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel refresh' : 'Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in w-full">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">{t('settings')}</h1>
        <p className="text-muted-foreground mt-1">
          {language === 'it' ? 'Configura il tuo account e le integrazioni' : 'Configure your account and integrations'}
        </p>
      </div>

      {/* OpenRouter Configuration */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading">OpenRouter API Key</CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? 'Configura la tua chiave API per accedere a 150+ modelli LLM'
                  : 'Configure your API key to access 150+ LLM models'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasOpenRouterKey ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-success/10 border border-success/30">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-success" />
                  <span className="text-success font-medium">
                    {language === 'it' ? 'API Key configurata' : 'API Key configured'}
                  </span>
                </div>
                {openRouterBalance !== null && (
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-success" />
                    <span className="font-code text-success">
                      {language === 'it' 
                        ? `€${(openRouterBalance * 0.92).toFixed(2)}`
                        : `$${openRouterBalance?.toFixed(2)}`}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleRefreshBalance}
                      disabled={refreshing}
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'it' 
                    ? 'Aggiorna la tua API key se necessario:'
                    : 'Update your API key if needed:'}
                </p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder={t('apiKeyPlaceholder')}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="bg-background"
                    data-testid="api-key-input"
                  />
                  <Button onClick={handleSaveApiKey} disabled={saving || !apiKey.trim()} data-testid="save-api-key-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-warning/10 border border-warning/30">
                <p className="text-warning text-sm">
                  {language === 'it' 
                    ? 'Per utilizzare LLM Council Manager, devi configurare una API key di OpenRouter.'
                    : 'To use LLM Council Manager, you need to configure an OpenRouter API key.'}
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    type="password"
                    placeholder={t('apiKeyPlaceholder')}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="bg-background"
                    data-testid="api-key-input"
                  />
                </div>
                
                <Button 
                  onClick={handleSaveApiKey} 
                  disabled={saving || !apiKey.trim()}
                  className="w-full gap-2"
                  data-testid="save-api-key-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'it' ? 'Salvataggio...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4" />
                      {t('saveApiKey')}
                    </>
                  )}
                </Button>
              </div>

              <div className="p-4 rounded-lg bg-card border border-border">
                <h4 className="font-medium mb-2">
                  {language === 'it' ? 'Come ottenere una API Key:' : 'How to get an API Key:'}
                </h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>{language === 'it' ? 'Vai su openrouter.ai' : 'Go to openrouter.ai'}</li>
                  <li>{language === 'it' ? 'Registrati o accedi' : 'Sign up or log in'}</li>
                  <li>{language === 'it' ? 'Dashboard → API Keys' : 'Dashboard → API Keys'}</li>
                  <li>{language === 'it' ? 'Crea una nuova chiave' : 'Create a new key'}</li>
                </ol>
                <a 
                  href="https://openrouter.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline mt-3 text-sm"
                >
                  {language === 'it' ? 'Apri OpenRouter' : 'Open OpenRouter'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/30 flex items-center justify-center">
              <Globe className="w-5 h-5 text-accent" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'Lingua' : 'Language'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? "Seleziona la lingua dell'interfaccia"
                  : 'Select interface language'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button
              variant={language === 'it' ? 'default' : 'outline'}
              onClick={() => setLanguage('it')}
              className="flex-1"
              data-testid="lang-btn-it"
            >
              🇮🇹 Italiano
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => setLanguage('en')}
              className="flex-1"
              data-testid="lang-btn-en"
            >
              🇬🇧 English
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accuracy Level Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 border border-warning/30 flex items-center justify-center">
              <Gauge className="w-5 h-5 text-warning" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'Livello di Accuratezza' : 'Accuracy Level'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? "Definisce quali modelli LLM il Manager del Progetto selezionerà"
                  : 'Defines which LLM models the Project Manager will select'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {ACCURACY_LEVELS.map((level) => (
              <button
                key={level.id}
                onClick={() => handleSaveAccuracyLevel(level.id)}
                disabled={savingAccuracy}
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  accuracyLevel === level.id 
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/30' 
                    : 'border-border hover:border-muted-foreground/50 bg-background'
                }`}
                data-testid={`accuracy-${level.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{level.icon}</span>
                    <div>
                      <span className={`font-medium ${accuracyLevel === level.id ? 'text-primary' : ''}`}>
                        {level.label[language] || level.label.en}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {level.description[language] || level.description.en}
                      </p>
                    </div>
                  </div>
                  {accuracyLevel === level.id && (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            {language === 'it' 
              ? '⚠️ Il Manager del Progetto utilizzerà questa preferenza per selezionare automaticamente i modelli più adatti per ogni esperto.'
              : '⚠️ The Project Manager will use this preference to automatically select the most suitable models for each expert.'}
          </p>
        </CardContent>
      </Card>

      {/* Team Size Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-info/10 border border-info/30 flex items-center justify-center">
              <User className="w-5 h-5 text-info" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'Dimensione Team' : 'Team Size'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? "Numero di esperti che il Manager genererà per ogni progetto"
                  : 'Number of experts the Manager will generate for each project'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {TEAM_SIZES.map((size) => (
              <button
                key={size.id}
                onClick={() => handleSaveTeamSize(size.id)}
                disabled={savingTeamSize}
                className={`p-4 rounded-lg border text-left transition-all ${
                  teamSize === size.id 
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/30' 
                    : 'border-border hover:border-muted-foreground/50 bg-background'
                }`}
                data-testid={`team-size-${size.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{size.icon}</span>
                    <div>
                      <span className={`font-medium ${teamSize === size.id ? 'text-primary' : ''}`}>
                        {size.label[language] || size.label.en}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {size.description[language] || size.description.en}
                      </p>
                      <span className="text-xs font-mono text-muted-foreground">
                        ({size.range} {language === 'it' ? 'esperti' : 'experts'})
                      </span>
                    </div>
                  </div>
                  {teamSize === size.id && (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            {language === 'it' 
              ? '💡 Team più grandi forniscono analisi più approfondite ma richiedono più tempo e costi.'
              : '💡 Larger teams provide deeper analysis but require more time and cost.'}
          </p>
        </CardContent>
      </Card>

      {/* Response Length Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 border border-success/30 flex items-center justify-center">
              <Pencil className="w-5 h-5 text-success" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'Lunghezza Pareri' : 'Response Length'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? "Lunghezza predefinita delle risposte degli esperti"
                  : 'Default length of expert responses'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {RESPONSE_LENGTHS.map((length) => (
              <button
                key={length.id}
                onClick={() => handleSaveResponseLength(length.id)}
                disabled={savingResponseLength}
                className={`p-4 rounded-lg border text-left transition-all ${
                  responseLength === length.id 
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/30' 
                    : 'border-border hover:border-muted-foreground/50 bg-background'
                }`}
                data-testid={`response-length-${length.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{length.icon}</span>
                    <div>
                      <span className={`font-medium ${responseLength === length.id ? 'text-primary' : ''}`}>
                        {length.label[language] || length.label.en}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {length.description[language] || length.description.en}
                      </p>
                    </div>
                  </div>
                  {responseLength === length.id && (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            {language === 'it' 
              ? '📝 Questa impostazione può essere sovrascritta per singoli esperti nelle impostazioni del progetto.'
              : '📝 This setting can be overridden for individual experts in project settings.'}
          </p>
        </CardContent>
      </Card>

      {/* Manager MCP Servers */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-info/10 border border-info/30 flex items-center justify-center">
              <Server className="w-5 h-5 text-info" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'MCP Servers Manager' : 'Manager MCP Servers'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? 'Configura i server MCP per estendere le capacità del Manager'
                  : 'Configure MCP servers to extend the Manager capabilities'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {language === 'it' 
                ? 'I server MCP (Model Context Protocol) permettono al Manager di accedere a strumenti e risorse esterne.'
                : 'MCP (Model Context Protocol) servers allow the Manager to access external tools and resources.'}
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addManagerMcpServer}
              className="gap-1 shrink-0"
            >
              <Plus className="w-3 h-3" />
              {language === 'it' ? 'Aggiungi' : 'Add'}
            </Button>
          </div>

          {managerMcpServers.length === 0 ? (
            <div className="p-6 rounded-lg border border-dashed border-border text-center">
              <Server className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {language === 'it' 
                  ? 'Nessun MCP server configurato per il Manager' 
                  : 'No MCP servers configured for the Manager'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'it' 
                  ? 'Clicca "Aggiungi" per configurare un server MCP' 
                  : 'Click "Add" to configure an MCP server'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {managerMcpServers.map((server, index) => (
                <div key={index} className="p-4 rounded-lg border border-border bg-background space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Server className="w-4 h-4 text-info" />
                      {server.name || `Server ${index + 1}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={() => removeManagerMcpServer(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">{language === 'it' ? 'Nome' : 'Name'}</Label>
                      <Input
                        value={server.name}
                        onChange={(e) => updateManagerMcpServer(index, 'name', e.target.value)}
                        placeholder="My MCP Server"
                        className="h-9 bg-card"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">URL</Label>
                      <Input
                        value={server.url}
                        onChange={(e) => updateManagerMcpServer(index, 'url', e.target.value)}
                        placeholder="https://mcp.example.com"
                        className="h-9 bg-card"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">API Key ({language === 'it' ? 'opzionale' : 'optional'})</Label>
                      <Input
                        type="password"
                        value={server.api_key || ''}
                        onChange={(e) => updateManagerMcpServer(index, 'api_key', e.target.value)}
                        placeholder="sk-..."
                        className="h-9 bg-card"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">{language === 'it' ? 'Descrizione' : 'Description'}</Label>
                      <Input
                        value={server.description || ''}
                        onChange={(e) => updateManagerMcpServer(index, 'description', e.target.value)}
                        placeholder={language === 'it' ? 'Descrizione server...' : 'Server description...'}
                        className="h-9 bg-card"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <Button 
                onClick={handleSaveManagerMcpServers}
                disabled={savingMcp}
                className="w-full gap-2"
              >
                {savingMcp ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {language === 'it' ? 'Salva Configurazione MCP' : 'Save MCP Configuration'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manager Prompt - 3 Phases */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading">
                {language === 'it' ? 'Prompt Manager del Progetto' : 'Project Manager Prompt'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? 'Configura le istruzioni per le 3 fasi del Manager: Analisi, Coordinamento e Sintesi'
                  : 'Configure instructions for the 3 Manager phases: Analysis, Coordination and Synthesis'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Warning */}
          <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
            <p className="text-sm text-warning-foreground">
              {language === 'it' 
                ? '⚠️ Questi prompt controllano il comportamento del Manager in ogni fase. Modificali con attenzione.'
                : '⚠️ These prompts control the Manager behavior in each phase. Edit carefully.'}
            </p>
          </div>
          
          {/* Custom prompts indicator */}
          {hasCustomPrompts && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-info/10 border border-info/30">
              <p className="text-sm text-info-foreground">
                {language === 'it' 
                  ? '✨ Stai usando prompt personalizzati'
                  : '✨ You are using custom prompts'}
              </p>
              <Button variant="outline" size="sm" onClick={handleResetToGlobal}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {language === 'it' ? 'Torna ai Globali' : 'Reset to Global'}
              </Button>
            </div>
          )}

          {/* Phase 1: Analysis */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-background/50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">1</span>
                  {language === 'it' ? 'Fase 1: Analisi e Composizione Team' : 'Phase 1: Analysis & Team Composition'}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'it' 
                    ? 'Analizza la richiesta, identifica esperti e seleziona modelli'
                    : 'Analyze request, identify experts and select models'}
                </p>
              </div>
              <Button 
                size="sm"
                onClick={() => handleSavePhasePrompt('phase1_analysis')}
                disabled={savingPhase === 'phase1_analysis'}
                className="gap-2"
              >
                {savingPhase === 'phase1_analysis' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" />
                )}
                {language === 'it' ? 'Salva' : 'Save'}
              </Button>
            </div>
            <Textarea
              value={managerPrompts.phase1_analysis}
              onChange={(e) => setManagerPrompts(p => ({ ...p, phase1_analysis: e.target.value }))}
              className="min-h-48 bg-card font-mono text-sm"
              placeholder={language === 'it' ? 'Prompt per la fase di analisi...' : 'Prompt for analysis phase...'}
            />
          </div>

          {/* Phase 2: Coordination */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-background/50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">2</span>
                  {language === 'it' ? 'Fase 2: Coordinamento e Raccolta Pareri' : 'Phase 2: Coordination & Opinion Gathering'}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'it' 
                    ? 'Coordina gli esperti e raccoglie le risposte'
                    : 'Coordinate experts and gather responses'}
                </p>
              </div>
              <Button 
                size="sm"
                onClick={() => handleSavePhasePrompt('phase2_coordination')}
                disabled={savingPhase === 'phase2_coordination'}
                className="gap-2"
              >
                {savingPhase === 'phase2_coordination' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" />
                )}
                {language === 'it' ? 'Salva' : 'Save'}
              </Button>
            </div>
            <Textarea
              value={managerPrompts.phase2_coordination}
              onChange={(e) => setManagerPrompts(p => ({ ...p, phase2_coordination: e.target.value }))}
              className="min-h-48 bg-card font-mono text-sm"
              placeholder={language === 'it' ? 'Prompt per la fase di coordinamento...' : 'Prompt for coordination phase...'}
            />
          </div>

          {/* Phase 3: Synthesis */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-background/50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">3</span>
                  {language === 'it' ? 'Fase 3: Valutazione e Sintesi Finale' : 'Phase 3: Evaluation & Final Synthesis'}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'it' 
                    ? 'Valuta le risposte e sintetizza il report finale'
                    : 'Evaluate responses and synthesize final report'}
                </p>
              </div>
              <Button 
                size="sm"
                onClick={() => handleSavePhasePrompt('phase3_synthesis')}
                disabled={savingPhase === 'phase3_synthesis'}
                className="gap-2"
              >
                {savingPhase === 'phase3_synthesis' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" />
                )}
                {language === 'it' ? 'Salva' : 'Save'}
              </Button>
            </div>
            <Textarea
              value={managerPrompts.phase3_synthesis}
              onChange={(e) => setManagerPrompts(p => ({ ...p, phase3_synthesis: e.target.value }))}
              className="min-h-48 bg-card font-mono text-sm"
              placeholder={language === 'it' ? 'Prompt per la fase di sintesi...' : 'Prompt for synthesis phase...'}
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-4 border-t border-border">
            <Button 
              onClick={handleSaveAllPrompts}
              disabled={savingPrompt}
              className="gap-2"
            >
              {savingPrompt ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {language === 'it' ? 'Salva Tutti i Prompt' : 'Save All Prompts'}
            </Button>
            <Button 
              variant="outline"
              onClick={resetManagerPrompt}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {language === 'it' ? 'Ripristina Default' : 'Reset to Default'}
            </Button>
            
            {/* SuperAdmin: Save as Global */}
            {user?.role === 'super_admin' && (
              <Button 
                variant="secondary"
                onClick={async () => {
                  try {
                    await adminApi.updateGlobalPrompts(managerPrompts);
                    toast.success(language === 'it' ? 'Prompt Globali aggiornati!' : 'Global prompts updated!');
                  } catch (error) {
                    toast.error(language === 'it' ? 'Errore nel salvataggio globale' : 'Failed to save global prompts');
                  }
                }}
                className="gap-2 ml-auto"
              >
                <Globe className="w-4 h-4" />
                {language === 'it' ? 'Salva come Globale' : 'Save as Global'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Custom Experts */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 border border-success/30 flex items-center justify-center">
                <User className="w-5 h-5 text-success" />
              </div>
              <div>
                <CardTitle className="font-heading">
                  {language === 'it' ? 'Esperti Custom' : 'Custom Experts'}
                </CardTitle>
                <CardDescription>
                  {language === 'it' 
                    ? 'Crea esperti personalizzati che l\'Analista può utilizzare'
                    : 'Create custom experts that the Analyzer can use'}
                </CardDescription>
              </div>
            </div>
            <Button onClick={openNewExpertDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              {language === 'it' ? 'Nuovo Esperto' : 'New Expert'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingExperts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : customExperts.length === 0 ? (
            <div className="p-6 rounded-lg border border-dashed border-border text-center">
              <Bot className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {language === 'it' 
                  ? 'Nessun esperto custom creato' 
                  : 'No custom experts created'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'it' 
                  ? 'Crea esperti personalizzati per analisi specifiche' 
                  : 'Create custom experts for specific analysis'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {customExperts.map((expert) => (
                <div key={expert.id} className="p-4 rounded-lg border border-border bg-background">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{expert.name}</h4>
                        {expert.is_global && (
                          <span className="px-2 py-0.5 rounded text-xs bg-success/20 text-success">
                            {language === 'it' ? 'Globale' : 'Global'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{expert.description}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">{language === 'it' ? 'Area:' : 'Area:'}</span> {expert.expertise_area}
                      </p>
                      {expert.preferred_model && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">{language === 'it' ? 'Modello:' : 'Model:'}</span> {expert.preferred_model}
                        </p>
                      )}
                      {expert.mcp_servers?.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">MCP:</span> {expert.mcp_servers.length} server(s)
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditExpertDialog(expert)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteExpert(expert.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expert Dialog */}
      <Dialog open={expertDialogOpen} onOpenChange={setExpertDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingExpert 
                ? (language === 'it' ? 'Modifica Esperto' : 'Edit Expert')
                : (language === 'it' ? 'Nuovo Esperto Custom' : 'New Custom Expert')}
            </DialogTitle>
            <DialogDescription>
              {language === 'it' 
                ? 'Il Manager potrà utilizzare questo esperto nelle sue analisi'
                : 'The Manager will be able to use this expert in its analysis'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>{language === 'it' ? 'Nome Esperto' : 'Expert Name'} *</Label>
              <Input
                value={expertForm.name}
                onChange={(e) => setExpertForm(f => ({ ...f, name: e.target.value }))}
                placeholder={language === 'it' ? 'Es: Esperto Blockchain' : 'E.g.: Blockchain Expert'}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{language === 'it' ? 'Descrizione' : 'Description'} *</Label>
              <Textarea
                value={expertForm.description}
                onChange={(e) => setExpertForm(f => ({ ...f, description: e.target.value }))}
                placeholder={language === 'it' 
                  ? 'Descrivi le competenze e il ruolo di questo esperto...' 
                  : 'Describe the skills and role of this expert...'}
                className="mt-1 min-h-24"
              />
            </div>
            <div>
              <Label>{language === 'it' ? 'Area di Competenza' : 'Expertise Area'} *</Label>
              <Input
                value={expertForm.expertise_area}
                onChange={(e) => setExpertForm(f => ({ ...f, expertise_area: e.target.value }))}
                placeholder={language === 'it' ? 'Es: Tecnologie DeFi, Smart Contracts' : 'E.g.: DeFi Technologies, Smart Contracts'}
                className="mt-1"
              />
            </div>
            
            {/* Model Selection with Dropdown */}
            <div>
              <Label>{language === 'it' ? 'Modello LLM' : 'LLM Model'}</Label>
              {allModels.length === 0 && !loadingModels && (
                <p className="text-xs text-warning mt-1 mb-2">
                  {language === 'it' 
                    ? '⚠️ Configura prima la tua API Key OpenRouter per vedere i modelli disponibili'
                    : '⚠️ Configure your OpenRouter API Key first to see available models'}
                </p>
              )}
              {allModels.length > 0 && (
                <div className="flex items-center gap-2 mt-1 mb-2">
                  <Switch
                    checked={modelFilter === 'open-source'}
                    onCheckedChange={handleOpenSourceFilterChange}
                  />
                  <span className="text-sm text-muted-foreground">
                    {language === 'it' ? 'Solo Open-Source' : 'Open-Source Only'}
                  </span>
                </div>
              )}
              <Popover open={modelSearchOpen} onOpenChange={setModelSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" disabled={allModels.length === 0 && !loadingModels}>
                    {expertForm.preferred_model || (language === 'it' ? 'Seleziona modello...' : 'Select model...')}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder={language === 'it' ? 'Cerca modello...' : 'Search model...'}
                      value={modelSearch}
                      onValueChange={setModelSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {loadingModels 
                          ? (language === 'it' ? 'Caricamento...' : 'Loading...')
                          : (language === 'it' ? 'Nessun modello trovato' : 'No model found')}
                      </CommandEmpty>
                      <CommandGroup>
                        <ScrollArea className="h-64">
                          <CommandItem
                            value=""
                            onSelect={() => {
                              setExpertForm(f => ({ ...f, preferred_model: '' }));
                              setModelSearchOpen(false);
                            }}
                          >
                            <span className="text-muted-foreground">
                              {language === 'it' ? '(Usa modello predefinito)' : '(Use default model)'}
                            </span>
                          </CommandItem>
                          {filteredModels.slice(0, 50).map((model) => (
                            <CommandItem
                              key={model.id}
                              value={model.id}
                              onSelect={() => {
                                setExpertForm(f => ({ ...f, preferred_model: model.id }));
                                setModelSearchOpen(false);
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{model.id}</span>
                                {model.pricing && (
                                  <span className="text-xs text-muted-foreground">
                                    ${(parseFloat(model.pricing.prompt) * 1000000).toFixed(2)}/M tokens
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </ScrollArea>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Global Switch */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <Label>{language === 'it' ? 'Esperto Globale' : 'Global Expert'}</Label>
                <p className="text-xs text-muted-foreground">
                  {language === 'it' 
                    ? 'Disponibile in tutti i progetti' 
                    : 'Available in all projects'}
                </p>
              </div>
              <Switch
                checked={expertForm.is_global}
                onCheckedChange={(checked) => setExpertForm(f => ({ ...f, is_global: checked }))}
              />
            </div>

            {/* MCP Servers for Expert */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  MCP Servers
                </Label>
                <Button variant="outline" size="sm" onClick={addMcpServerToExpert} className="gap-1">
                  <Plus className="w-3 h-3" />
                  {language === 'it' ? 'Aggiungi' : 'Add'}
                </Button>
              </div>
              
              {expertForm.mcp_servers.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {language === 'it' ? 'Nessun MCP server configurato' : 'No MCP servers configured'}
                </p>
              ) : (
                <div className="space-y-3">
                  {expertForm.mcp_servers.map((server, index) => (
                    <div key={index} className="p-3 rounded-lg border border-border bg-background space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{server.name || `Server ${index + 1}`}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => removeMcpServerFromExpert(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={language === 'it' ? 'Nome' : 'Name'}
                          value={server.name}
                          onChange={(e) => updateExpertMcpServer(index, 'name', e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Input
                          placeholder="URL"
                          value={server.url}
                          onChange={(e) => updateExpertMcpServer(index, 'url', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="password"
                          placeholder="API Key"
                          value={server.api_key || ''}
                          onChange={(e) => updateExpertMcpServer(index, 'api_key', e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Input
                          placeholder={language === 'it' ? 'Descrizione' : 'Description'}
                          value={server.description || ''}
                          onChange={(e) => updateExpertMcpServer(index, 'description', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setExpertDialogOpen(false)}>
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </Button>
              <Button onClick={handleSaveExpert} disabled={savingExpert} className="gap-2">
                {savingExpert ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {language === 'it' ? 'Salva' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
